import React from "react";
import {
  AutoComplete,
  Button,
  Col,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Tag,
  Typography
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { parseClock, spanMinutes } from "./calc";
import { centsToEuro, decimalInputProps, euroToCents, formatEuro, formatMinutes } from "./format";

const UNITS = ["Stk", "m", "m²", "m³", "lfm", "kg", "l", "Sack", "Rolle", "Pkg", "psch", "Std."];

/** "8:15", "8,25", "8.5" or "495" (minutes when > 24) → minutes; null when unreadable. */
export const parseDuration = value => {
  const text = String(value || "").trim();
  if (!text) return 0;
  const clock = text.match(/^(\d{1,2}):(\d{2})$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const n = Number(text.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return null;
  return n > 24 ? Math.round(n) : Math.round(n * 60);
};

const Field = ({ label, children }) => (
  <label className="wr-field">
    <span className="wr-field-label">{label}</span>
    {children}
  </label>
);

export const LabourLine = ({ line, computed, employees, readOnly, lng, onChange, onRemove }) => {
  const { t } = useTranslation("dashboard");
  const timed = Boolean(line.start || line.end);
  const span = spanMinutes(line.start, line.end);
  const set = patch => onChange({ ...line, ...patch });

  const pickEmployee = value => {
    if (!value) return set({ employee: null });
    const employee = employees.find(e => e._id === value);
    set({ employee: value, name: employee?.name || line.name });
  };

  return (
    <div className="wr-line">
      <div className="wr-line-head">
        <Segmented
          size="small"
          value={line.kind}
          disabled={readOnly}
          options={[
            { value: "WORK", label: t("Work") },
            { value: "TRAVEL", label: t("Travel") }
          ]}
          onChange={kind => set({ kind })}
        />
        <span className="wr-line-figures">
          {computed && (
            <>
              <strong className="wr-num">{formatMinutes(computed.minutes)} h</strong>
              {computed.billedMinutes !== computed.minutes && (
                <Typography.Text type="secondary" className="wr-num">
                  {" "}
                  → {formatMinutes(computed.billedMinutes)}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" className="wr-num">
                {" · "}
                {formatEuro(computed.net, lng)}
              </Typography.Text>
            </>
          )}
          {line.source === "ATTENDANCE" && (
            <Tag bordered={false} color="blue" className="wr-source">
              {t("Time tracking")}
            </Tag>
          )}
        </span>
        {!readOnly && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={t("Remove")}
            onClick={onRemove}
          />
        )}
      </div>
      <Row gutter={[8, 8]}>
        <Col xs={24} md={8}>
          <Field label={t("Employee")}>
            <Select
              className="wr-full"
              showSearch
              allowClear
              optionFilterProp="label"
              value={line.employee || undefined}
              disabled={readOnly}
              placeholder={t("Choose or type a name below")}
              options={employees.map(e => ({ value: e._id, label: e.name }))}
              onChange={pickEmployee}
            />
          </Field>
          {!line.employee && (
            <Input
              className="wr-name-input"
              value={line.name}
              disabled={readOnly}
              maxLength={120}
              placeholder={t("Name (subcontractor, helper …)")}
              onChange={event => set({ name: event.target.value })}
            />
          )}
        </Col>
        <Col xs={8} md={3}>
          <Field label={t("From")}>
            <Input
              type="time"
              value={line.start}
              disabled={readOnly}
              onChange={event => set({ start: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={8} md={3}>
          <Field label={t("To")}>
            <Input
              type="time"
              value={line.end}
              disabled={readOnly}
              onChange={event => set({ end: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={8} md={3}>
          <Field label={t("Break (min)")}>
            <InputNumber
              className="wr-full"
              min={0}
              max={600}
              step={15}
              inputMode="numeric"
              value={line.breakMinutes || null}
              disabled={readOnly || !timed}
              onChange={value => set({ breakMinutes: value || 0 })}
            />
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field label={t("Duration (h:mm)")}>
            {timed ? (
              <Input
                value={
                  parseClock(line.start) !== null && span !== null
                    ? formatMinutes(Math.max(0, span - (line.breakMinutes || 0)))
                    : ""
                }
                disabled
              />
            ) : (
              <Input
                inputMode="decimal"
                defaultValue={line.minutes ? formatMinutes(line.minutes) : ""}
                key={`d-${line.key}-${line.minutes}`}
                disabled={readOnly}
                placeholder="1:30"
                onBlur={event => {
                  const minutes = parseDuration(event.target.value);
                  if (minutes !== null) set({ minutes });
                }}
              />
            )}
          </Field>
        </Col>
        <Col xs={12} md={4}>
          <Field label={t("Rate per hour")}>
            <InputNumber
              className="wr-full"
              min={0}
              step={0.5}
              precision={2}
              {...decimalInputProps(lng)}
              addonAfter="€"
              inputMode="decimal"
              value={centsToEuro(line.rate)}
              disabled={readOnly}
              onChange={value => set({ rate: euroToCents(value) })}
            />
          </Field>
        </Col>
        <Col xs={24}>
          <Input
            value={line.note}
            disabled={readOnly}
            maxLength={300}
            placeholder={t("Note (optional)")}
            onChange={event => set({ note: event.target.value })}
          />
        </Col>
      </Row>
    </div>
  );
};

export const MaterialLine = ({ line, computed, readOnly, lng, onChange, onRemove }) => {
  const { t } = useTranslation("dashboard");
  const set = patch => onChange({ ...line, ...patch });
  return (
    <div className="wr-line">
      <div className="wr-line-head">
        <Select
          size="small"
          className="wr-kind-select"
          value={line.kind}
          disabled={readOnly}
          options={[
            { value: "MATERIAL", label: t("Material") },
            { value: "FLAT", label: t("Flat rate") },
            { value: "EQUIPMENT", label: t("Equipment") }
          ]}
          onChange={kind => set({ kind })}
        />
        <span className="wr-line-figures">
          {computed && <strong className="wr-num">{formatEuro(computed.net, lng)}</strong>}
        </span>
        {!readOnly && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={t("Remove")}
            onClick={onRemove}
          />
        )}
      </div>
      <Row gutter={[8, 8]}>
        <Col xs={24} md={10}>
          <Field label={t("Description")}>
            <Input
              value={line.description}
              disabled={readOnly}
              maxLength={300}
              onChange={event => set({ description: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={12} md={4}>
          <Field label={t("Article no.")}>
            <Input
              value={line.articleNumber}
              disabled={readOnly}
              maxLength={60}
              onChange={event => set({ articleNumber: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field label={t("Quantity")}>
            <InputNumber
              className="wr-full"
              min={0}
              precision={3}
              {...decimalInputProps(lng)}
              inputMode="decimal"
              value={line.quantity}
              disabled={readOnly}
              onChange={value => set({ quantity: value || 0 })}
            />
          </Field>
        </Col>
        <Col xs={12} md={3}>
          <Field label={t("Unit")}>
            <AutoComplete
              className="wr-full"
              value={line.unit}
              disabled={readOnly}
              options={UNITS.map(unit => ({ value: unit }))}
              filterOption={(input, option) =>
                option.value.toLowerCase().startsWith(String(input).toLowerCase())
              }
              onChange={unit => set({ unit: String(unit || "").slice(0, 20) })}
            />
          </Field>
        </Col>
        <Col xs={12} md={4}>
          <Field label={t("Unit price")}>
            <InputNumber
              className="wr-full"
              min={0}
              precision={2}
              {...decimalInputProps(lng)}
              addonAfter="€"
              inputMode="decimal"
              value={centsToEuro(line.unitPrice)}
              disabled={readOnly}
              onChange={value => set({ unitPrice: euroToCents(value) })}
            />
          </Field>
        </Col>
      </Row>
    </div>
  );
};
